// store global types
import type { TCreateSlice, TAsyncStateWithProgressMessage } from "@/store";

// dependencies types
import type { TTableLocation } from "@/apis/odin/apiODIN";

type TSuccessCoAFilePayload = {
    coaId: number;
    allFoundTables: TTableLocation[];
};

type TActionsUploadCoA = {
    setCoAFileId: (payload: number | null) => void;
    startUploading: () => void;
    setUploadProgress: (payload: {
        progress: number;
        progressMessage: string;
    }) => void;
    setUploadSuccess: (params: TSuccessCoAFilePayload) => void;
    checkFileValidity: ({ files }: { files: FileList }) => File;
    setUploadFailure: (errorMessage: string) => void;
    resetStateUploadCoA: () => void;
    setCoAFileName: (payload: string) => void;
};

type TStateUploadCoA = {
    coaFileName: string | null;
    coaFileId: number | null;
    uploadCoAState: TAsyncStateWithProgressMessage<
        TSuccessCoAFilePayload,
        string
    >;
};

export type TSliceUploadCoA = TStateUploadCoA & TActionsUploadCoA;

export const initialStateUploadCoA = {
    coaFileName: null,
    coaFileId: null,
    uploadCoAState: {
        status: "idle" as const,
        progress: null,
        progressMessage: null,
        error: null,
        data: null,
    },
} satisfies TStateUploadCoA;

export const createSliceUploadCoA: TCreateSlice<TSliceUploadCoA> =
    () => set => {
        const actions: TActionsUploadCoA = {
            setCoAFileId: payload =>
                set(
                    state => ({ ...state, coaFileId: payload }),
                    false,
                    "uploadFile/setFileId",
                ),

            startUploading: () =>
                set(
                    state => ({
                        ...state,
                        uploadCoAState: {
                            ...state.uploadCoAState,
                            data: null,
                            error: null,
                            status: "pending",
                        },
                    }),
                    false,
                    "uploadFile/startUploading",
                ),
            setUploadProgress: payload =>
                set(
                    state => ({
                        ...state,
                        uploadCoAState: {
                            ...state.uploadCoAState,
                            progress: payload.progress,
                            progressMessage: payload.progressMessage,
                        },
                    }),
                    false,
                    "uploadFile/uploadProgress",
                ),
            setUploadSuccess: payload =>
                set(
                    state => ({
                        ...state,
                        uploadCoAState: {
                            ...state.uploadCoAState,
                            status: "resolved",
                            error: null,
                            data: payload,
                        },
                    }),
                    false,
                    "uploadFile/uploadSuccess",
                ),
            setUploadFailure: payload => {
                set(
                    state => ({
                        ...state,
                        uploadCoAState: {
                            ...state.uploadCoAState,
                            status: "rejected",
                            error: payload,
                            data: null,
                            progress: null,
                            progressMessage: null,
                        },
                    }),
                    false,
                    "uploadFile/uploadFailure",
                );
            },
            checkFileValidity: ({ files }: { files: FileList }): File => {
                const supportedFileTypes = [
                    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                    "application/vnd.ms-excel.sheet.macroenabled.12",
                    "application/vnd.ms-excel",
                ];

                if (files.length > 1) {
                    throw new Error(
                        "Only one file is allowed. Please select a single Excel file.",
                    );
                }

                const file = files[0];
                if (!file) {
                    throw new Error("No file selected.");
                }

                if (
                    !file.type ||
                    supportedFileTypes.indexOf(file.type.toLowerCase()) === -1
                ) {
                    throw new Error(
                        "It looks like this file type isn't supported.",
                    );
                }
                return file;
            },
            resetStateUploadCoA: () =>
                set(
                    { ...initialStateUploadCoA },
                    false,
                    "uploadFile/resetStateUploadCoA",
                ),
            setCoAFileName: payload =>
                set(
                    state => ({ ...state, coaFileName: payload }),
                    false,
                    "uploadFile/setCoAFileName",
                ),
        };

        return {
            ...initialStateUploadCoA,
            ...actions,
        };
    };
